import React, { useState, useEffect, useMemo } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, getUsers, updateUserDisplayName, createUser, deleteUser } from '../../api';
import {
  Text,
  Heading,
  VStack,
  Button,
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Spinner,
  Alert,
  AlertIcon,
  Icon,
  Select,
  HStack,
  useToast,
  Input,
  FormControl,
  FormLabel,
} from '@chakra-ui/react';
import { Navigate, Link } from 'react-router-dom';
import { ChevronUpIcon, ChevronDownIcon, CheckIcon, CloseIcon, AddIcon, DeleteIcon } from '@chakra-ui/icons';
import { ModalInfo } from '../../components/ModalInfo';
import Footer from '../../components/Footer';

const VALID_DISPLAY_NAMES = new Set(['admin', 'operator', 'cashier', 'promoter', 'warehouse']);

function UsersPage() {
  const [user, loading] = useAuthState(auth);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [sortColumn, setSortColumn] = useState('displayName');
  const [sortDirection, setSortDirection] = useState('asc');
  const [editingUserId, setEditingUserId] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [showDeleteResultModal, setShowDeleteResultModal] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [newUserDisplayName, setNewUserDisplayName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [createResult, setCreateResult] = useState({ success: false, message: '' });
  const [deleteResult, setDeleteResult] = useState({ success: false, message: '' });
  const toast = useToast();

  useEffect(() => {
    const loadUsers = async () => {
      setIsLoading(true);
      setError('');
      const result = await getUsers();
      if (result.success) {
        setUsers(result.users || []);
      } else {
        setError(result.error || 'Errore nel caricamento degli utenti');
      }
      setIsLoading(false);
    };

    if (user?.displayName === 'admin') {
      loadUsers();
    }
  }, [user]);

  const handleOpenCreateModal = () => {
    setNewUserEmail('');
    setNewUserPassword('');
    setNewUserDisplayName('');
    setShowCreateModal(true);
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setNewUserEmail('');
    setNewUserPassword('');
    setNewUserDisplayName('');
  };

  const handleCreateUser = () => {
    if (!newUserEmail || !newUserPassword || !newUserDisplayName) {
      toast({
        title: 'Errore',
        description: 'Compila tutti i campi',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!VALID_DISPLAY_NAMES.has(newUserDisplayName)) {
      toast({
        title: 'Errore',
        description: 'Seleziona un displayName valido',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setShowCreateModal(false);
    setShowConfirmModal(true);
  };

  const handleConfirmCreate = async () => {
    setIsCreating(true);
    setShowConfirmModal(false);
    setShowResultModal(true);

    const result = await createUser(newUserEmail, newUserPassword, newUserDisplayName);
    setIsCreating(false);

    if (result.success) {
      setCreateResult({
        success: true,
        message: `Utente creato con successo!\nEmail: ${result.user.email}\nDisplayName: ${result.user.displayName}`,
      });
      setUsers((prevUsers) => [...prevUsers, result.user]);
      setNewUserEmail('');
      setNewUserPassword('');
      setNewUserDisplayName('');
    } else {
      setCreateResult({
        success: false,
        message: result.error || 'Errore nella creazione dell\'utente',
      });
    }
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const sortedUsers = useMemo(() => {
    if (!sortColumn) return users;

    return [...users].sort((a, b) => {
      const aValue = a[sortColumn] || '';
      const bValue = b[sortColumn] || '';
      const comparison = aValue.localeCompare(bValue, 'it', { sensitivity: 'base' });
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [users, sortColumn, sortDirection]);

  const handleEdit = (userId, currentDisplayName) => {
    setEditingUserId(userId);
    setEditingValue(currentDisplayName || '');
  };

  const handleCancel = () => {
    setEditingUserId(null);
    setEditingValue('');
  };

  const handleSave = async (userId) => {
    if (!editingValue || !VALID_DISPLAY_NAMES.has(editingValue)) {
      toast({
        title: 'Errore',
        description: 'Seleziona un displayName valido',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsSaving(true);
    const result = await updateUserDisplayName(userId, editingValue);
    setIsSaving(false);

    if (result.success) {
      setUsers((prevUsers) =>
        prevUsers.map((u) => (u.uid === userId ? { ...u, displayName: editingValue } : u))
      );
      setEditingUserId(null);
      setEditingValue('');
      toast({
        title: 'Successo',
        description: 'DisplayName aggiornato con successo',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } else {
      toast({
        title: 'Errore',
        description: result.error || 'Errore nell\'aggiornamento del displayName',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleDeleteClick = (userToDelete) => {
    setUserToDelete(userToDelete);
    setShowDeleteConfirmModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;

    setIsDeleting(true);
    setShowDeleteConfirmModal(false);
    setShowDeleteResultModal(true);

    const result = await deleteUser(userToDelete.uid);
    setIsDeleting(false);

    if (result.success) {
      setDeleteResult({
        success: true,
        message: `Utente eliminato con successo!\nEmail: ${userToDelete.email}`,
      });
      setUsers((prevUsers) => prevUsers.filter((u) => u.uid !== userToDelete.uid));
      setUserToDelete(null);
    } else {
      setDeleteResult({
        success: false,
        message: result.error || 'Errore nell\'eliminazione dell\'utente',
      });
    }
  };

  if (loading) {
    return (
      <VStack w='100%' spacing={4} mt={10}>
        <Spinner size='xl' color='teal' />
        <Text color='white'>Verifica autenticazione...</Text>
      </VStack>
    );
  }

  if (!user) {
    return <Navigate to='/dashboard' replace={true} />;
  }

  if (user.displayName !== 'admin') {
    return <Navigate to='/dashboard' replace={true} />;
  }

  return (
    <VStack w='100%'>
      <Heading mt={5} mb={0} color={'white'}>
        Lista Utenti
      </Heading>

      <Text mb={3}>{process.env.REACT_APP_PROJECT_ID}</Text>
      <Button mb={3} as={Link} to='/dashboard'>
        Dashboard
      </Button>

      <Box
        w='100%'
        border='3px solid teal'
        borderRadius={15}
        p={5}
        mb={10}
        color='white'
        bg='rgba(0, 0, 0, 0.3)'
      >
        {isLoading ? (
          <VStack spacing={4}>
            <Spinner size='xl' color='teal' />
            <Text>Caricamento utenti...</Text>
          </VStack>
        ) : error ? (
          <Alert status='error'>
            <AlertIcon />
            {error}
          </Alert>
        ) : (
          <VStack align='stretch' spacing={4}>
            <HStack justify='space-between' mb={3}>
              <Text fontSize='xl' fontWeight={600}>
                Totale utenti: {users.length}
              </Text>
              <Button
                leftIcon={<AddIcon />}
                
                onClick={handleOpenCreateModal}
              >
                Crea Utente
              </Button>
            </HStack>
            <TableContainer>
              <Table variant='simple' colorScheme='teal'>
                <Thead>
                  <Tr>
                    <Th
                      color='white'
                      cursor='pointer'
                      userSelect='none'
                      onClick={() => handleSort('email')}
                      _hover={{ bg: 'rgba(255, 255, 255, 0.1)' }}
                    >
                      Email
                      {sortColumn === 'email' && (
                        <Icon
                          as={sortDirection === 'asc' ? ChevronUpIcon : ChevronDownIcon}
                          ml={2}
                          boxSize={4}
                        />
                      )}
                    </Th>
                    <Th
                      color='white'
                      cursor='pointer'
                      userSelect='none'
                      onClick={() => handleSort('displayName')}
                      _hover={{ bg: 'rgba(255, 255, 255, 0.1)' }}
                    >
                      Ruolo
                      {sortColumn === 'displayName' && (
                        <Icon
                          as={sortDirection === 'asc' ? ChevronUpIcon : ChevronDownIcon}
                          ml={2}
                          boxSize={4}
                        />
                      )}
                    </Th>
                    <Th color='white'>Azioni</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {sortedUsers.length === 0 ? (
                    <Tr>
                      <Td colSpan={3} textAlign='center'>
                        <Text>Nessun utente trovato</Text>
                      </Td>
                    </Tr>
                  ) : (
                    sortedUsers.map((u) => (
                      <Tr key={u.uid}>
                        <Td>{u.email || '-'}</Td>
                        <Td>
                          {editingUserId === u.uid ? (
                            <HStack spacing={2}>
                              <Select
                                value={editingValue}
                                onChange={(e) => setEditingValue(e.target.value)}
                                size='sm'
                                bg='white'
                                color='black'
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    handleSave(u.uid);
                                  } else if (e.key === 'Escape') {
                                    handleCancel();
                                  }
                                }}
                              >
                                <option value=''>Seleziona...</option>
                                {Array.from(VALID_DISPLAY_NAMES).map((name) => (
                                  <option key={name} value={name}>
                                    {name}
                                  </option>
                                ))}
                              </Select>
                              <Button
                                size='xs'
                                colorScheme='green'
                                onClick={() => handleSave(u.uid)}
                                isLoading={isSaving}
                                isDisabled={isSaving || !editingValue}
                              >
                                <CheckIcon />
                              </Button>
                              <Button
                                size='xs'
                                colorScheme='red'
                                onClick={handleCancel}
                                isDisabled={isSaving}
                              >
                                <CloseIcon />
                              </Button>
                            </HStack>
                          ) : (
                            <Text
                              cursor='pointer'
                              _hover={{ textDecoration: 'underline' }}
                              onClick={() => handleEdit(u.uid, u.displayName)}
                            >
                              {u.displayName || '-'}
                            </Text>
                          )}
                        </Td>
                        <Td>
                          <Button
                            size='xs'
                            colorScheme='red'
                            variant='outline'
                            onClick={() => handleDeleteClick(u)}
                          >
                            <DeleteIcon />
                          </Button>
                        </Td>
                      </Tr>
                    ))
                  )}
                </Tbody>
              </Table>
            </TableContainer>
          </VStack>
        )}
      </Box>

      <Footer />

      {/* Modal per creare utente */}
      <ModalInfo
        title='Crea Nuovo Utente'
        isOpen={showCreateModal}
        onClose={handleCreateUser}
        onCloseText='Crea'
        onCancel={handleCloseCreateModal}
        onCancelText='Annulla'
      >
        <VStack spacing={4} align='stretch'>
          <FormControl>
            <FormLabel>Email</FormLabel>
            <Input
              type='email'
              value={newUserEmail}
              onChange={(e) => setNewUserEmail(e.target.value)}
              placeholder='esempio@email.com'
            />
          </FormControl>
          <FormControl>
            <FormLabel>Password</FormLabel>
            <Input
              type='password'
              value={newUserPassword}
              onChange={(e) => setNewUserPassword(e.target.value)}
              placeholder='Password'
            />
          </FormControl>
          <FormControl>
            <FormLabel>Ruolo</FormLabel>
            <Select
              value={newUserDisplayName}
              onChange={(e) => setNewUserDisplayName(e.target.value)}
              placeholder='Seleziona...'
            >
              {Array.from(VALID_DISPLAY_NAMES).map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </Select>
          </FormControl>
        </VStack>
      </ModalInfo>

      {/* Modal di conferma */}
      <ModalInfo
        title='Conferma Creazione Utente'
        body={`Confermi la creazione dell'utente?\n\nEmail: ${newUserEmail}\nDisplayName: ${newUserDisplayName}`}
        isOpen={showConfirmModal}
        onClose={handleConfirmCreate}
        onCloseText='Conferma'
        onCancel={() => {
          setShowConfirmModal(false);
          setShowCreateModal(true);
        }}
        onCancelText='Annulla'
      >
        {isCreating && (
          <VStack spacing={2} mt={4}>
            <Spinner size='lg' color='teal' />
            <Text>Creazione utente in corso...</Text>
          </VStack>
        )}
      </ModalInfo>

      {/* Modal risultato creazione */}
      <ModalInfo
        title={createResult.success ? 'Successo' : 'Errore'}
        body={createResult.message}
        isOpen={showResultModal}
        onClose={() => {
          setShowResultModal(false);
          setCreateResult({ success: false, message: '' });
        }}
        onCloseText='OK'
      >
        {isCreating && (
          <VStack spacing={2} mt={4}>
            <Spinner size='lg' color='teal' />
            <Text>Creazione utente in corso...</Text>
          </VStack>
        )}
      </ModalInfo>

      {/* Modal conferma eliminazione */}
      <ModalInfo
        title='Conferma Eliminazione Utente'
        body={userToDelete ? `Confermi l'eliminazione dell'utente?\n\nEmail: ${userToDelete.email}\nDisplayName: ${userToDelete.displayName || '-'}` : ''}
        isOpen={showDeleteConfirmModal}
        onClose={handleConfirmDelete}
        onCloseText='Elimina'
        onCancel={() => {
          setShowDeleteConfirmModal(false);
          setUserToDelete(null);
        }}
        onCancelText='Annulla'
      />

      {/* Modal risultato eliminazione */}
      <ModalInfo
        title={deleteResult.success ? 'Successo' : 'Errore'}
        body={deleteResult.message}
        isOpen={showDeleteResultModal}
        onClose={() => {
          setShowDeleteResultModal(false);
          setDeleteResult({ success: false, message: '' });
        }}
        onCloseText='OK'
      >
        {isDeleting && (
          <VStack spacing={2} mt={4}>
            <Spinner size='lg' color='teal' />
            <Text>Eliminazione utente in corso...</Text>
          </VStack>
        )}
      </ModalInfo>
    </VStack>
  );
}

export default UsersPage;
